import { useMemo } from "react";
import type { Diagnostic, EdgeDirection, NodeMetadata } from "@karasu-tools/core";
import type { ActiveView, HighlightAttribute } from "../state/app-reducer.js";
import type { PreviewContextValue } from "../state/preview-context.js";
import type { SharePayload } from "../utils/inline-share.js";
import type { UseCrossNavigationResult } from "./useCrossNavigation.js";
import type { SystemViewBundle, DeployViewBundle, OrgViewBundle } from "./useAppViews.js";

type BreadcrumbItems = { id: string; label: string }[];

interface UsePreviewContextValueArgs {
  activeView: ActiveView;
  viewPath: string[];
  selectedDeployBlockId: string | null;
  highlightedNodeId: string | null;
  /** The attribute `highlightedNodeId` is matched against, set by its producer (#2818). */
  highlightAttribute: HighlightAttribute;
  nodeMetadata: Map<string, NodeMetadata>;
  /** Metadata keyed by a card's `data-node-path` (#2917); empty on the deploy view. */
  nodeMetadataByPath: Map<string, NodeMetadata>;

  // Sliced from the `useAppViews` bundle types (#2015 point 8) instead of
  // re-listing each field's type by hand — the field names/types stay the
  // single source of truth in `useAppViews.ts`.
  system: Pick<
    SystemViewBundle,
    | "svg"
    | "diagnostics"
    | "warnings"
    | "hasDeployDiagram"
    | "nodeDiff"
    | "nodeDiffByPath"
    | "resolvedSystems"
    | "toggleCategory"
    | "toggleGroup"
    | "toggleExpand"
    | "groupBy"
    | "setGroupBy"
    | "selectedFacets"
    | "toggleFacet"
    | "facets"
    | "facetOverview"
    | "hasTeamAxis"
    | "hasBoundaryAxis"
    | "anyCollapsible"
    | "allCollapsed"
    | "expansionOverload"
    | "onCollapseAllToggle"
  >;
  deploy: Pick<DeployViewBundle, "svg" | "diagnostics" | "warnings" | "deployBlocks">;
  org: Pick<
    OrgViewBundle,
    | "svg"
    | "diagnostics"
    | "warnings"
    | "orgTreeSvg"
    | "orgTreeExportSvg"
    | "toggleTeamExpand"
    | "teamDependencySvg"
    | "hasTeamDependencyView"
  >;

  breadcrumbItems: BreadcrumbItems;
  orgBreadcrumbItems: BreadcrumbItems;
  nav: UseCrossNavigationResult;
  navigateActiveView: (view: ActiveView) => void;
  navigateViewPath: (path: string[]) => void;

  isAllLayersOpen: boolean;
  toggleAllLayers: () => void;
  getDrillDownSvg?: () => string | undefined;
  allLayersSvg?: string;
  orgAllLayersSvg?: string;
  getOrgDrillDownSvg?: () => string | undefined;
  getAllViewsSvg?: () => string | undefined;
  exportBundlesAvailable: boolean;

  previewFocused: boolean;
  togglePreviewFocus: () => void;
  /** Already gated by the host (undefined in editor-less modes). */
  onJumpToEditor?: (nodeId: string) => void;

  isOrgTreeViewOpen: boolean;
  toggleOrgTreeView: () => void;

  isTeamDependenciesOpen: boolean;
  toggleTeamDependencies: () => void;

  isEntityViewOpen: boolean;
  toggleEntityView: () => void;
  entityViewSvg?: string;
  entityViewDiagnostics: Diagnostic[];
  hasEntityView: boolean;

  styleTargetPath?: string;
  onPickEdgeDirection?: (canonicalId: string, direction: EdgeDirection) => void;
  readOnly?: boolean;

  onExportSvg: (svg: string, filename: string) => void;
  /** Already built by the host (undefined when no project is available). */
  onExportDrawio?: (filename: string) => Promise<void>;
  /** Whether the active file has shareable source (Share button enabled state). */
  hasKrsSource?: boolean;
  /** Flattens the project into a share payload (PreviewColumn's Share button). */
  getShareBundle?: () => Promise<SharePayload>;
  /** Names the document a finding's position is in (banner and warning panel, #2715). */
  currentFilePath: string | null;
  displayRoot: string | null;
}

/**
 * Assembles the {@link PreviewContextValue} consumed by `PreviewColumn`. This
 * is the 35-property object with the wide dependency array that previously
 * dominated AppShell; isolating it here keeps AppShell to layout wiring (#1541).
 */
export function usePreviewContextValue(args: UsePreviewContextValueArgs): PreviewContextValue {
  const {
    activeView,
    viewPath,
    selectedDeployBlockId,
    highlightedNodeId,
    highlightAttribute,
    nodeMetadata,
    nodeMetadataByPath,
    system,
    deploy,
    org,
    breadcrumbItems,
    orgBreadcrumbItems,
    nav,
    navigateActiveView,
    navigateViewPath,
    isAllLayersOpen,
    toggleAllLayers,
    getDrillDownSvg,
    allLayersSvg,
    orgAllLayersSvg,
    getOrgDrillDownSvg,
    getAllViewsSvg,
    exportBundlesAvailable,
    previewFocused,
    togglePreviewFocus,
    onJumpToEditor,
    isOrgTreeViewOpen,
    toggleOrgTreeView,
    isTeamDependenciesOpen,
    toggleTeamDependencies,
    isEntityViewOpen,
    toggleEntityView,
    entityViewSvg,
    entityViewDiagnostics,
    hasEntityView,
    styleTargetPath,
    onPickEdgeDirection,
    readOnly,
    onExportSvg,
    onExportDrawio,
    hasKrsSource,
    getShareBundle,
    currentFilePath,
    displayRoot,
  } = args;

  return useMemo<PreviewContextValue>(
    () => ({
      activeView,
      hasDeployDiagram: system.hasDeployDiagram,
      onActiveViewChange: navigateActiveView,
      systemView: {
        svg: system.svg,
        diagnostics: system.diagnostics,
        viewPath,
        breadcrumbItems,
        warnings: system.warnings,
        onBreadcrumbNavigate: navigateViewPath,
        onDeployButtonClick: nav.handleDeployButtonClick,
        onTeamButtonClick: nav.handleTeamButtonClick,
        onCategoryToggle: system.toggleCategory,
        onGroupToggle: system.toggleGroup,
        onExpandToggle: system.toggleExpand,
        groupBy: system.groupBy,
        onGroupByChange: system.setGroupBy,
        selectedFacets: system.selectedFacets,
        onFacetToggle: system.toggleFacet,
        facets: system.facets,
        facetOverview: system.facetOverview,
        hasTeamAxis: system.hasTeamAxis,
        hasBoundaryAxis: system.hasBoundaryAxis,
        anyCollapsible: system.anyCollapsible,
        allCollapsed: system.allCollapsed,
        expansionOverload: system.expansionOverload,
        onCollapseAllToggle: system.onCollapseAllToggle,
        highlightedNodeId,
        highlightAttribute,
        onClearHighlight: nav.clearHighlight,
        nodeDiff: system.nodeDiff,
        nodeDiffByPath: system.nodeDiffByPath,
        systems: system.resolvedSystems,
      },
      deployView: {
        svg: deploy.svg,
        diagnostics: deploy.diagnostics,
        warnings: deploy.warnings,
        highlightedNodeId,
        highlightAttribute,
        onClearHighlight: nav.clearHighlight,
        onContainerClick: nav.handleContainerClick,
      },
      orgView: {
        svg: org.svg,
        diagnostics: org.diagnostics,
        viewPath,
        breadcrumbItems: orgBreadcrumbItems,
        warnings: org.warnings,
        onBreadcrumbNavigate: navigateViewPath,
        highlightedNodeId,
        highlightAttribute,
        onClearHighlight: nav.clearHighlight,
        onOwnedServiceClick: nav.handleOwnedServiceClick,
      },
      nodeMetadata,
      nodeMetadataByPath,
      deployBlocks: deploy.deployBlocks,
      selectedDeployBlockId,
      onDeployBlockChange: nav.handleDeployBlockChange,
      onExportSvg,
      onExportDrawio,
      isAllLayersOpen,
      onAllLayersToggle: toggleAllLayers,
      getDrillDownSvg,
      allLayersSvg,
      orgAllLayersSvg,
      getOrgDrillDownSvg,
      getAllViewsSvg,
      exportBundlesAvailable,
      previewFocused,
      onPreviewFocusToggle: togglePreviewFocus,
      onJumpToEditor,
      isOrgTreeViewOpen,
      onOrgTreeViewToggle: toggleOrgTreeView,
      orgTreeSvg: org.orgTreeSvg,
      onTeamToggle: org.toggleTeamExpand,
      orgTreeExportSvg: org.orgTreeExportSvg,
      isTeamDependenciesOpen,
      onTeamDependenciesToggle: toggleTeamDependencies,
      teamDependencySvg: org.teamDependencySvg,
      hasTeamDependencyView: org.hasTeamDependencyView,
      isEntityViewOpen,
      onEntityViewToggle: toggleEntityView,
      entityViewSvg,
      entityViewDiagnostics,
      hasEntityView,
      styleTargetPath,
      onPickEdgeDirection,
      readOnly,
      hasKrsSource,
      getShareBundle,
      currentFilePath,
      displayRoot,
    }),
    [
      activeView,
      navigateActiveView,
      navigateViewPath,
      system.hasDeployDiagram,
      system.svg,
      system.diagnostics,
      system.warnings,
      system.nodeDiff,
      system.nodeDiffByPath,
      system.resolvedSystems,
      deploy.svg,
      deploy.diagnostics,
      deploy.warnings,
      deploy.deployBlocks,
      org.svg,
      org.diagnostics,
      org.warnings,
      org.orgTreeSvg,
      org.toggleTeamExpand,
      system.toggleCategory,
      system.toggleGroup,
      system.toggleExpand,
      system.groupBy,
      system.setGroupBy,
      system.selectedFacets,
      system.toggleFacet,
      system.facets,
      system.facetOverview,
      system.hasTeamAxis,
      system.hasBoundaryAxis,
      system.anyCollapsible,
      system.allCollapsed,
      system.expansionOverload,
      system.onCollapseAllToggle,
      org.orgTreeExportSvg,
      org.teamDependencySvg,
      org.hasTeamDependencyView,
      isTeamDependenciesOpen,
      toggleTeamDependencies,
      viewPath,
      breadcrumbItems,
      orgBreadcrumbItems,
      highlightedNodeId,
      highlightAttribute,
      nodeMetadata,
      nodeMetadataByPath,
      selectedDeployBlockId,
      nav,
      isAllLayersOpen,
      toggleAllLayers,
      getDrillDownSvg,
      allLayersSvg,
      orgAllLayersSvg,
      getOrgDrillDownSvg,
      getAllViewsSvg,
      exportBundlesAvailable,
      previewFocused,
      togglePreviewFocus,
      onJumpToEditor,
      isOrgTreeViewOpen,
      toggleOrgTreeView,
      isEntityViewOpen,
      toggleEntityView,
      entityViewSvg,
      entityViewDiagnostics,
      hasEntityView,
      styleTargetPath,
      onPickEdgeDirection,
      readOnly,
      onExportSvg,
      onExportDrawio,
      hasKrsSource,
      getShareBundle,
      currentFilePath,
      displayRoot,
    ],
  );
}
